import { lastFullMonth } from "../routes/priceVarianceReport/reportSelection";
import { accountingRangeError, recentAccountingRange } from "./accountingPeriod";

export type View = "belege" | "credits" | "articles" | "offers" | "price_report" | "files" | "cost_centers" | "order_confirmations" | "article_period" | "article_history";
export type SavedView = { revision: number; params: Record<string, string> };
const periodKeys = ["period", "from", "to"];
export const keysFor = (view: View) => ({
  credits: periodKeys,
  article_period: periodKeys,
  article_history: ["history_group", "history_merge"],
  belege: [...periodKeys, "q", "supplier", "type", "status", "review", "comments", "sort", "mine", "cost_center_type", "cost_center_key"],
  articles: ["q", "sort", "manufacturer", "supplier", "cost_center", "bought"],
  offers: ["q", "sort"],
  order_confirmations: ["q", "status"],
  cost_centers: ["q", "type", "window", "customer_view"],
  files: ["q", "format", "doc_type", "supplier", "parser_status", "source_from", "source_to", "sort_by", "sort_dir"],
  price_report: [...periodKeys, "supplier", "project", "min_delta_pct", "min_difference"],
})[view];
export const articleMultiKeys = ["manufacturer", "supplier", "cost_center"];

export function personalParams(view: View, params: URLSearchParams): Record<string, string> | null {
  const result: Record<string, string> = {};
  for (const key of keysFor(view)) {
    if (view === "articles" && articleMultiKeys.includes(key)) {
      const values = [...new Set(params.getAll(key).map(v => v.trim()).filter(Boolean))].sort();
      if (values.length) result[key] = JSON.stringify(values);
      continue;
    }
    const value = params.get(key)?.trim();
    if (value) result[key] = value;
  }
  if (view === "article_history") return result;
  if (view === "files") return fileDateError(params) ? null : result;
  if (!result.period && (params.has("from") || params.has("to"))) result.period = "custom";
  if (result.period === "custom") {
    if (accountingRangeError(result.from ?? "", result.to ?? "")) return null;
  } else { delete result.from; delete result.to; }
  return result;
}

export function restoredParams(view: View, saved: SavedView, current: URLSearchParams) {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(saved.params)) {
    if (!keysFor(view).includes(key)) continue;
    if (view === "articles" && articleMultiKeys.includes(key)) {
      try { const values: unknown = JSON.parse(value); if (Array.isArray(values)) values.filter((v): v is string => typeof v === "string").forEach(v => next.append(key, v)); } catch { /* Ignore obsolete malformed presentation values. */ }
    } else next.set(key, value);
  }
  if (view === "price_report") {
    next.set("report", "0");
    if (next.get("period") === "last_month") { const dates = lastFullMonth(); next.set("from", dates.from); next.set("to", dates.to); }
  }
  if (view === "belege" && ["1m", "1y"].includes(next.get("period") ?? "")) {
    const range = recentAccountingRange(next.get("period") === "1y" ? 12 : 1);
    next.set("from", range.from); next.set("to", range.to);
  }
  return next;
}


export function fileDateError(params: URLSearchParams): string | null {
  const from = params.get("source_from") ?? "", to = params.get("source_to") ?? "";
  if ([from, to].some(value => value && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) || (from && to && from > to)) return "Bitte wähle einen gültigen Belegzeitraum. Das Enddatum darf nicht vor dem Startdatum liegen.";
  return null;
}
